import { ModuleDefinition, ModuleInstance, ModuleStatus } from './types';

/**
 * Central registry for all modules
 */
class ModuleRegistry {
  private modules: Map<string, ModuleInstance> = new Map();
  private moduleDefinitions: Map<string, ModuleDefinition> = new Map();

  /**
   * Register a module definition
   */
  register(definition: ModuleDefinition): void {
    // Validate module definition
    if (!definition.id || !definition.name || !definition.version) {
      throw new Error('Module definition must have id, name, and version');
    }

    // Check for duplicate IDs
    if (this.moduleDefinitions.has(definition.id)) {
      console.warn(`Module ${definition.id} is already registered. Overwriting...`);
    }

    // Auto-attach getConfig if not already provided by the module
    if (!definition.getConfig) {
      const registry = this;
      const moduleId = definition.id;
      definition.getConfig = () => registry.getConfig(moduleId);
    }

    // Store definition
    this.moduleDefinitions.set(definition.id, definition);

    // Create instance
    const instance: ModuleInstance = {
      definition,
      status: 'disabled'
    };

    this.modules.set(definition.id, instance);

    // Call onLoad hook if provided
    if (definition.onLoad) {
      try {
        definition.onLoad();
      } catch (error) {
        console.error(`Error calling onLoad for module ${definition.id}:`, error);
        instance.status = 'error';
        instance.error = error as Error;
      }
    }

    console.log(`Module registered: ${definition.id} v${definition.version}`);
  }

  /**
   * Get a module definition by ID
   */
  getDefinition(moduleId: string): ModuleDefinition | undefined {
    return this.moduleDefinitions.get(moduleId);
  }

  /**
   * Get a module instance by ID
   */
  getInstance(moduleId: string): ModuleInstance | undefined {
    return this.modules.get(moduleId);
  }

  /**
   * Get all registered module definitions
   */
  getAllDefinitions(): ModuleDefinition[] {
    return Array.from(this.moduleDefinitions.values());
  }

  /**
   * Get all module instances
   */
  getAllInstances(): ModuleInstance[] {
    return Array.from(this.modules.values());
  }

  /**
   * Check if a module is registered
   */
  has(moduleId: string): boolean {
    return this.moduleDefinitions.has(moduleId);
  }

  /**
   * Update module status
   */
  updateStatus(moduleId: string, status: ModuleStatus, error?: Error): void {
    const instance = this.modules.get(moduleId);
    if (instance) {
      instance.status = status;
      if (error) {
        instance.error = error;
      }
    }
  }

  /**
   * Get enabled modules for a client
   * A module is enabled if it has a client config for the given clientId
   */
  getEnabledModules(clientId: string | null): ModuleDefinition[] {
    if (!clientId) return [];
    
    return this.getAllDefinitions().filter(def => {
      const instance = this.modules.get(def.id);
      // Module is enabled if it has a client config for this clientId
      const clientConfig = instance?.clientConfig;
      return clientConfig?.clientId === clientId;
    });
  }

  /**
   * Check if a module is enabled for a client
   * A module is enabled if it has a client config for the given clientId
   */
  isEnabled(moduleId: string, clientId: string | null): boolean {
    if (!clientId) return false;
    const instance = this.modules.get(moduleId);
    const definition = this.moduleDefinitions.get(moduleId);
    
    if (!definition) return false;
    
    // Module is enabled if it has a client config for this clientId
    const clientConfig = instance?.clientConfig;
    return clientConfig?.clientId === clientId;
  }

  /**
   * Get the client config object for a module by ID.
   * Shorthand for `getInstance(moduleId)?.clientConfig?.config`.
   */
  getConfig(moduleId: string): Record<string, any> | undefined {
    return this.modules.get(moduleId)?.clientConfig?.config;
  }

  /**
   * Resolve extension for a module by extension point key.
   */
  private resolveExtension(definition: ModuleDefinition, extensionPoint: string): unknown {
    return definition.extensions?.[extensionPoint];
  }

  /**
   * Get extension for a module by extension point key.
   */
  getExtension<T>(moduleId: string, extensionPoint: string): T | undefined {
    const def = this.moduleDefinitions.get(moduleId);
    if (!def) return undefined;
    return this.resolveExtension(def, extensionPoint) as T | undefined;
  }

  /**
   * Get all modules that have an extension for this point, optionally filtered by clientId.
   */
  getExtensionsForPoint<T>(
    extensionPoint: string,
    clientId?: string | null
  ): Array<{ moduleId: string; extension: T }> {
    return this.getAllDefinitions()
      .filter(def => !clientId || this.isEnabled(def.id, clientId))
      .map(def => {
        const ext = this.resolveExtension(def, extensionPoint);
        return ext ? { moduleId: def.id, extension: ext as T } : null;
      })
      .filter((r): r is { moduleId: string; extension: T } => r !== null);
  }

  /**
   * Clear all modules (useful for testing)
   */
  clear(): void {
    this.modules.clear();
    this.moduleDefinitions.clear();
  }
}

// Singleton instance
export const moduleRegistry = new ModuleRegistry();
